import React, { useState, useEffect, useCallback } from 'react';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';
import axiosInstance from '../../../utils/axios';
import { toast } from 'react-hot-toast';

const QuickActionToolbar = ({ 
  onPrintSheets, 
  onExportReports, 
  onGenerateCompliance,
  onRefresh 
}) => {
  const [isScanning, setIsScanning] = useState(false);
  const [scanCount, setScanCount] = useState(0);
  const [loading, setLoading] = useState(false);
  const [integrationStatus, setIntegrationStatus] = useState([
    {
      system: 'ERP Integration',
      status: 'Checking...',
      lastSync: 'Connecting...',
      icon: 'Database',
      color: 'text-muted-foreground'
    },
    {
      system: 'Mobile Scanner',
      status: 'Ready',
      lastSync: 'Not connected',
      icon: 'Smartphone',
      color: 'text-muted-foreground'
    },
    {
      system: 'Backup Service',
      status: 'Checking...',
      lastSync: 'Connecting...',
      icon: 'Cloud',
      color: 'text-muted-foreground'
    }
  ]);

  const keyboardShortcuts = [
    { key: 'Ctrl + S', action: 'Start Scanning' },
    { key: 'Ctrl + P', action: 'Print Sheets' },
    { key: 'Ctrl + E', action: 'Export Reports' },
    { key: 'Enter', action: 'Verify Selected' },
    { key: 'Tab', action: 'Navigate Assets' },
    { key: 'Esc', action: 'Cancel Action' }
  ];

  // Load integration status from API
  const loadIntegrationStatus = useCallback(async () => {
    try {
      const response = await axiosInstance.get('/audit/integration-status');
      if (response.data.success) {
        setIntegrationStatus(response.data.data);
      }
    } catch (error) {
      console.error('Error loading integration status:', error);
      // Set fallback status
      setIntegrationStatus([
        {
          system: 'ERP Integration',
          status: 'Offline',
          lastSync: 'Connection failed',
          icon: 'Database',
          color: 'text-error'
        },
        {
          system: 'Mobile Scanner',
          status: 'Ready',
          lastSync: 'Not connected',
          icon: 'Smartphone',
          color: 'text-muted-foreground'
        },
        {
          system: 'Backup Service',
          status: 'Offline',
          lastSync: 'Connection failed',
          icon: 'Cloud',
          color: 'text-error'
        }
      ]);
    }
  }, []);

  // Handle barcode scanning
  const handleBarcodeScanning = async () => {
    if (isScanning) {
      // Stop scanning
      setIsScanning(false);
      setScanCount(0);
      toast.info('Scanning session ended');
      return;
    }

    try {
      setLoading(true);
      const response = await axiosInstance.post('/audit/start-scanning');
      
      if (response.data.success) {
        setIsScanning(true);
        setScanCount(0);
        toast.success('Scanning session started');
        
        // Simulate scanning process with real API calls
        const scanInterval = setInterval(async () => {
          try {
            const scanResponse = await axiosInstance.get('/audit/scan-progress');
            if (scanResponse.data.success) {
              const newCount = scanResponse.data.data.scanned_count || scanCount + 1;
              setScanCount(newCount);
              
              // Auto-stop after 10 scans or if session ends
              if (newCount >= 10 || scanResponse.data.data.session_ended) {
                clearInterval(scanInterval);
                setIsScanning(false);
                toast.success(`Scanning complete: ${newCount} assets scanned`);
                if (onRefresh) onRefresh();
              }
            }
          } catch (error) {
            console.error('Error during scanning:', error);
          }
        }, 2000);
        
        // Auto-stop after 30 seconds
        setTimeout(() => {
          clearInterval(scanInterval);
          if (isScanning) {
            setIsScanning(false);
            toast.info('Scanning session timed out');
          }
        }, 30000);
        
      } else {
        toast.error(response.data.message || 'Failed to start scanning');
      }
    } catch (error) {
      console.error('Error starting scanning:', error);
      toast.error(error.response?.data?.message || 'Failed to start scanning');
    } finally {
      setLoading(false);
    }
  };

  // Handle print audit sheets
  const handlePrintSheets = () => {
    window.open('/api/audit/print-sheets?format=html', '_blank');
    toast.success('Printable audit sheet opened in new window');
    if (onPrintSheets) onPrintSheets();
  };

  // Handle export reports
  const handleExportReports = async () => {
    try {
      setLoading(true);
      const response = await axiosInstance.get('/audit/export', {
        params: { type: 'queue' },
        responseType: 'blob'
      });

      const url = window.URL.createObjectURL(new Blob([response.data], { type: 'text/csv' }));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `quarterly-audit-report-${new Date().toISOString().split('T')[0]}.csv`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      
      toast.success('Audit report exported as CSV successfully');
      if (onExportReports) onExportReports();
    } catch (error) {
      console.error('Error exporting reports:', error);
      toast.error(error.response?.data?.message || 'Failed to export report');
    } finally {
      setLoading(false);
    }
  };

  // Handle generate compliance
  const handleGenerateCompliance = async () => {
    try {
      setLoading(true);
      const response = await axiosInstance.get('/audit/report');
      
      if (response.data.success) {
        toast.success('Quarterly compliance documentation generated');
        if (onGenerateCompliance) onGenerateCompliance(response.data.data);
      } else {
        toast.error(response.data.message || 'Failed to generate compliance report');
      }
    } catch (error) {
      console.error('Error generating compliance report:', error);
      toast.error(error.response?.data?.message || 'Failed to generate compliance report');
    } finally {
      setLoading(false);
    }
  };

  // Load integration status on mount
  useEffect(() => {
    loadIntegrationStatus();
    
    // Refresh status every 30 seconds
    const interval = setInterval(loadIntegrationStatus, 30000);
    return () => clearInterval(interval);
  }, [loadIntegrationStatus]);

  const quickActions = [
    {
      id: 'scan',
      label: isScanning ? `Scanning... (${scanCount})` : 'Start Barcode Scan',
      icon: isScanning ? 'Loader2' : 'Scan',
      variant: isScanning ? 'default' : 'outline',
      onClick: handleBarcodeScanning,
      disabled: loading || isScanning,
      tooltip: isScanning ? 'Scanning in progress...' : 'Start mobile barcode scanning session'
    },
    {
      id: 'print',
      label: 'Print Audit Sheets',
      icon: 'Printer',
      variant: 'outline',
      onClick: handlePrintSheets,
      disabled: loading,
      tooltip: 'Generate printable audit verification sheets'
    },
    {
      id: 'export',
      label: 'Export Reports',
      icon: 'Download',
      variant: 'outline',
      onClick: handleExportReports,
      disabled: loading,
      tooltip: 'Export audit data and verification reports'
    },
    {
      id: 'compliance',
      label: 'Generate Compliance',
      icon: 'FileCheck',
      variant: 'default',
      onClick: handleGenerateCompliance,
      disabled: loading,
      tooltip: 'Generate SOX compliance documentation'
    }
  ];

  return (
    <div className="bg-card border border-border rounded-lg shadow-subtle">
      <div className="p-6">
        <div className="flex items-center justify-between mb-6">
          <div>
            <h3 className="text-lg font-semibold text-card-foreground">Quick Actions</h3>
            <p className="text-sm text-muted-foreground">
              Streamlined audit workflow tools and shortcuts
            </p>
          </div>
          <div className="flex items-center space-x-2">
            <div className="flex items-center space-x-1 text-sm text-muted-foreground">
              <Icon name="Zap" size={16} />
              <span>Keyboard shortcuts enabled</span>
            </div>
            <Button
              variant="ghost"
              size="sm"
              onClick={loadIntegrationStatus}
              disabled={loading}
            >
              <Icon name="RefreshCw" size={14} className={loading ? 'animate-spin' : ''} />
            </Button>
          </div>
        </div>

        {/* Quick Action Buttons */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4 mb-6">
          {quickActions.map((action) => (
            <div key={action.id} className="relative group">
              <Button
                variant={action.variant}
                onClick={action.onClick}
                disabled={action.disabled}
                className="w-full h-20 flex flex-col items-center justify-center space-y-2"
              >
                <Icon 
                  name={action.icon} 
                  size={24} 
                  className={action.icon === 'Loader2' && isScanning ? 'animate-spin' : ''}
                />
                <span className="text-sm font-medium">{action.label}</span>
              </Button>
              
              {/* Tooltip */}
              <div className="absolute bottom-full left-1/2 transform -translate-x-1/2 mb-2 px-3 py-2 bg-popover text-popover-foreground text-xs rounded-md shadow-elevated opacity-0 group-hover:opacity-100 transition-smooth pointer-events-none whitespace-nowrap z-50">
                {action.tooltip}
              </div>
            </div>
          ))}
        </div>

        {/* Integration Status */}
        <div className="mb-6">
          <h4 className="text-sm font-medium text-card-foreground mb-3">System Integration Status</h4>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            {integrationStatus.map((system, index) => (
              <div key={index} className="flex items-center space-x-3 p-3 bg-muted/30 rounded-lg">
                <div className={system.color || 'text-muted-foreground'}>
                  <Icon name={system.icon} size={20} />
                </div>
                <div className="flex-1 min-w-0">
                  <div className="flex items-center justify-between">
                    <span className="text-sm font-medium text-card-foreground">
                      {system.system}
                    </span>
                    <span className={`text-xs px-2 py-1 rounded-full ${
                      system.status === 'Connected' || system.status === 'Synced' || system.status === 'Ready' 
                        ? 'bg-success/20 text-success'
                        : system.status === 'Active' 
                        ? 'bg-accent/20 text-accent' 
                        : system.status === 'Checking...' || system.status === 'Connecting...'
                        ? 'bg-warning/20 text-warning'
                        : 'bg-error/20 text-error'
                    }`}>
                      {system.status}
                    </span>
                  </div>
                  <p className="text-xs text-muted-foreground">
                    Last sync: {system.lastSync}
                  </p>
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* Keyboard Shortcuts */}
        <div>
          <h4 className="text-sm font-medium text-card-foreground mb-3">Keyboard Shortcuts</h4>
          <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
            {keyboardShortcuts.map((shortcut, index) => (
              <div key={index} className="flex items-center justify-between p-2 bg-muted/30 rounded-md">
                <span className="text-xs text-muted-foreground">{shortcut.action}</span>
                <kbd className="px-2 py-1 text-xs font-mono bg-card border border-border rounded">
                  {shortcut.key}
                </kbd>
              </div>
            ))}
          </div>
        </div>

        {/* Scanning Progress */}
        {isScanning && (
          <div className="mt-6 p-4 bg-accent/10 border border-accent/20 rounded-lg animate-in fade-in slide-in-from-top-2 duration-300">
            <div className="flex items-center justify-between mb-2">
              <div className="flex items-center space-x-2">
                <Icon name="Scan" size={16} className="text-accent animate-pulse" />
                <span className="text-sm font-medium text-accent">Barcode Scanning Active</span>
              </div>
              <Button
                variant="ghost"
                size="sm"
                onClick={handleBarcodeScanning}
                className="text-accent hover:text-accent"
                disabled={loading}
              >
                <Icon name="X" size={16} />
              </Button>
            </div>
            <div className="flex items-center space-x-3">
              <div className="flex-1 bg-muted rounded-full h-2 overflow-hidden">
                <div 
                  className="bg-accent h-2 rounded-full transition-all duration-500"
                  style={{ width: `${Math.min((scanCount / 10) * 100, 100)}%` }}
                />
              </div>
              <span className="text-sm text-muted-foreground whitespace-nowrap">
                {scanCount} assets scanned
              </span>
            </div>
            <p className="text-xs text-muted-foreground mt-2">
              Scanning session will auto-stop after 10 scans or 30 seconds
            </p>
          </div>
        )}

        {/* Loading Overlay */}
        {loading && !isScanning && (
          <div className="mt-4 p-3 bg-muted/50 rounded-lg flex items-center justify-center space-x-2">
            <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-primary"></div>
            <span className="text-sm text-muted-foreground">Processing...</span>
          </div>
        )}
      </div>
    </div>
  );
};

export default QuickActionToolbar;